import {
  cardSurfaceClass,
  cardTitleTextClass,
  compactInputClass,
  insetSurfaceCardClass,
  inputClass,
  secondaryBtnClass,
} from "@/components/common/surfaceClasses";
import type { Dispatch, SetStateAction } from "react";
import type { KbLane } from "@/lib/kb/types";
import type { GuidanceProduct, GuidancePurpose } from "./trainingPanelGuidance";

type TrainingAskQueryControlsProps = {
  showPricingAsk?: boolean;
  dictation: any;
  setResult: any;
  setError: any;
  confirmDictationAsk: () => void;
  query: string;
  setQuery: (v: string) => void;
  loading: boolean;
  onAsk: () => void;
  laneSelection: KbLane;
  onLaneSelectionChange: (lane: KbLane) => void;
  laneHelpOpen: boolean;
  setLaneHelpOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  hasAskThread: boolean;
  nextAskKind: "new" | "followup";
  onThreadModeButtonClick: () => void;
  guidanceOpen: boolean;
  setGuidanceOpen: (v: boolean | ((p: boolean) => boolean)) => void;
  purposeSelection: GuidancePurpose | null;
  productSelection: GuidanceProduct | null;
  onPurposeChange: (next: GuidancePurpose | "") => void;
  onProductChange: (next: GuidanceProduct | "") => void;
  laneOverrideOnce: KbLane | null;
  setLaneOverrideOnce: Dispatch<SetStateAction<KbLane | null>>;
  guidanceNotice: string | null;
};

export function TrainingAskQueryControls(props: TrainingAskQueryControlsProps) {
  const showPricingAsk = props.showPricingAsk === true;
  return (
    <>
      <div className={`${cardSurfaceClass} p-3 mb-3 space-y-2`}>
        <div className="flex items-center justify-between">
          <div className={`text-sm font-semibold ${cardTitleTextClass}`}>Spoken question</div>
          {!props.dictation.isSupported && <div className="text-xs text-gray-500 dark:text-zinc-400">Not supported in this browser</div>}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className={`rounded-xl px-3 py-2 text-sm font-semibold ${props.dictation.isSupported && !props.dictation.isListening ? "bg-black text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-400"}`} disabled={!props.dictation.isSupported || props.dictation.isListening} onClick={() => { props.setResult(null); props.setError(null); props.dictation.start(); }}>
            Dictate
          </button>
          <button type="button" className={`rounded-xl px-3 py-2 text-sm font-semibold ${props.dictation.isListening ? "bg-black text-white dark:bg-zinc-100 dark:text-zinc-900" : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-400"}`} disabled={!props.dictation.isListening} onClick={props.dictation.stop}>
            Stop
          </button>
          <button type="button" className={`rounded-xl px-3 py-2 text-sm font-semibold ${props.dictation.draft.trim() ? secondaryBtnClass : "bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-zinc-700 dark:text-zinc-400"}`} disabled={!props.dictation.draft.trim()} onClick={props.confirmDictationAsk}>
            Confirm → Ask
          </button>
        </div>
        {props.dictation.error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl p-2 dark:bg-red-950/40 dark:border-red-800 dark:text-red-300">Dictation error: {props.dictation.error}</div> : null}
        {props.dictation.draft.trim() ? (
          <div className={`text-sm ${insetSurfaceCardClass}`}>
            <div className="text-xs text-gray-500 dark:text-zinc-400 mb-1">Heard</div>
            <div className={`whitespace-pre-wrap ${cardTitleTextClass}`}>{props.dictation.draft}</div>
            <div className="text-xs text-gray-500 dark:text-zinc-400 mt-2">Confirm to ask immediately, then you can refine and ask again.</div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <input className={`flex-1 ${inputClass} rounded-xl text-base`} value={props.query} placeholder='e.g., "What is our policy on Facebook ads per day?"' onChange={(e) => { props.setQuery(e.target.value); props.setResult(null); props.setError(null); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); props.onAsk(); } }} />
        <button type="button" disabled={!props.query.trim() || props.loading} className={"px-4 py-2 rounded-xl font-semibold " + (!props.query.trim() || props.loading ? "bg-gray-300 text-gray-600" : "bg-indigo-600 text-white hover:bg-indigo-700")} onClick={() => void props.onAsk()}>
          {props.loading ? "Searching…" : "Ask"}
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-zinc-400">
        <label className="inline-flex items-center gap-1.5">
          Lane
          <select className={`${compactInputClass} rounded-md px-2 py-1`} value={props.laneSelection ?? "answers"} onChange={(e) => props.onLaneSelectionChange((e.target.value || "answers") as KbLane)}>
            <option value="answers">Answers</option>
            <option value="contacts">Contacts</option>
            {showPricingAsk ? <option value="pricing">Pricing</option> : null}
          </select>
        </label>
        <div className="relative">
          <button type="button" className={`${secondaryBtnClass} rounded-full px-1.5 py-0.5 text-[11px] font-semibold`} aria-label="What does lane do?" title="What does lane do?" onClick={() => props.setLaneHelpOpen((v: boolean) => !v)}>?</button>
          {props.laneHelpOpen ? (
            <div className={`absolute left-0 top-6 z-10 w-64 ${insetSurfaceCardClass} p-2 text-[11px] text-gray-700 dark:text-zinc-300 shadow-lg`}>
              Lane sets retrieval focus for this ask. Use <strong>Answers</strong> for policy/how-to and <strong>Contacts</strong> for who-to-contact routing.
              {showPricingAsk ? (
                <>
                  {" "}
                  Use <strong>Pricing</strong> for price questions (platform only).
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <span className="text-gray-700 dark:text-zinc-300">
          <strong>
            {!props.hasAskThread ? "New thread" : props.nextAskKind === "followup" ? "Continue current thread" : "Start new thread on next ask"}
          </strong>
        </span>
        <button type="button" className={`rounded-md border px-2 py-1 text-xs font-medium ${!props.hasAskThread ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-500" : props.nextAskKind === "followup" ? "border-indigo-300 bg-indigo-50 text-indigo-900 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-200" : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"}`} disabled={!props.hasAskThread} title={!props.hasAskThread ? "Ask a question first to start a thread" : props.nextAskKind === "followup" ? "Next Ask continues this thread. Click to start a new thread on the next Ask instead." : "Next Ask will start a new thread. Click to continue this thread on the next Ask instead."} onClick={() => props.onThreadModeButtonClick()}>
          {props.hasAskThread ? (props.nextAskKind === "followup" ? "Switch to new thread" : "Switch to continue thread") : "Start a new thread"}
        </button>
      </div>

      <div className="mt-2">
        <button type="button" className="text-xs text-indigo-700 underline dark:text-indigo-300" onClick={() => props.setGuidanceOpen((v: boolean) => !v)}>
          {props.guidanceOpen ? "Hide Knowledge Base Guidance Filters" : "Open Knowledge Base Guidance Filters"}
        </button>
        {props.guidanceOpen ? (
          <div className="mt-2 rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 space-y-2 dark:border-indigo-800/70 dark:bg-indigo-950/30">
            <div className="text-xs font-semibold text-indigo-900 dark:text-indigo-200">Knowledge Base Guidance Filters</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="text-xs text-gray-700 dark:text-zinc-300">Purpose
                <select className={`mt-1 w-full ${inputClass}`} value={props.purposeSelection ?? ""} onChange={(e) => props.onPurposeChange((e.target.value || "") as GuidancePurpose | "")}>
                  <option value="">Auto</option><option value="contacts">Contacts</option><option value="policy">Policy</option><option value="advice">Advice</option>
                  {showPricingAsk ? <option value="pricing">Pricing</option> : null}
                </select>
              </label>
              <label className="text-xs text-gray-700 dark:text-zinc-300">Product
                <select className={`mt-1 w-full ${inputClass}`} value={props.productSelection ?? ""} onChange={(e) => props.onProductChange((e.target.value || "") as GuidanceProduct | "")}>
                  <option value="">Auto</option><option value="all">All</option><option value="sheds">Sheds</option><option value="steel">Steel</option>
                </select>
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="text-xs text-gray-700 dark:text-zinc-300">One-question lane override
                <select
                  className={`mt-1 w-full ${inputClass}`}
                  value={props.laneOverrideOnce ?? ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    props.setLaneOverrideOnce(v === "" ? null : (v as KbLane));
                  }}
                >
                  <option value="">(none)</option><option value="answers">Answers</option><option value="contacts">Contacts</option>
                  {showPricingAsk ? <option value="pricing">Pricing</option> : null}
                </select>
              </label>
            </div>
          </div>
        ) : null}
        {props.guidanceNotice ? <div className="mt-2 text-xs text-indigo-700 dark:text-indigo-300">{props.guidanceNotice}</div> : null}
      </div>
    </>
  );
}
